import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { UiIcons } from '@pipebase/ui';
import { useAuth } from '../auth/AuthContext';
import { RuntimeSwitcher } from './RuntimeSwitcher';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';
import { NewAssetModal } from '../components/NewAssetModal';
import { useCloud, useConsoleUrl } from '../cloud';

function initialsFor(source: string): string {
  const parts = source
    .trim()
    .split(/\s+|\.|_|-|@/)
    .filter(Boolean)
    .slice(0, 2);
  if (parts.length === 0) return '??';
  return parts.map((p) => p[0]!.toUpperCase()).join('').slice(0, 2);
}

/** Right-side topbar content: workspace/runtime switchers, + New, notifications,
 *  help, and user avatar / SSO login. Passed to Topbar's rightSlot prop. */
export function TopbarActions() {
  const { user, ssoConfig, logout } = useAuth();
  const consoleUrl = useConsoleUrl();
  const cloud = useCloud();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [newAssetOpen, setNewAssetOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!userMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(e.target as Node)
      )
        setUserMenuOpen(false);
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [userMenuOpen]);

  // The principal is a discriminated union: SSO users carry name/email,
  // local users carry a username. Derive a single display label + optional
  // secondary line so the avatar/menu never touches a missing field.
  const displayName = user
    ? user.kind === 'local'
      ? user.username
      : user.name || user.email
    : '';
  const secondaryLine = user && user.kind === 'sso' ? user.email : null;
  const initials = user ? initialsFor(displayName) : '??';
  const ssoConfigured = ssoConfig?.configured === true;

  return (
    <>
      <WorkspaceSwitcher />
      <RuntimeSwitcher />
      <button
        type="button"
        className="btn btn--primary"
        style={{ height: 28, fontSize: 12, marginLeft: 8 }}
        onClick={() => setNewAssetOpen(true)}
        data-testid="topbar-new-asset"
        title="Create a new flow or event design"
      >
        + New
      </button>
      <div
        style={{
          width: 1,
          height: 20,
          background: 'var(--border-subtle)',
          margin: '0 8px',
        }}
      />
      {/* The docs, in a new tab: the Pipebase Cloud section on a cloud designer.
          (A notifications bell with a permanent dot used to sit here; there
          are no notifications, so it is gone.) */}
      <a
        className="topbar__icon-btn"
        href={cloud ? 'https://docs.pipebase.dev/cloud/' : 'https://docs.pipebase.dev/'}
        target="_blank"
        rel="noreferrer"
        title="Help: the docs"
        aria-label="Help: the docs (opens in a new tab)"
        data-testid="topbar-help"
      >
        {UiIcons.help}
      </a>

      <div ref={userMenuRef} style={{ position: 'relative' }}>
        {user ? (
          <button
            type="button"
            className="user-avatar"
            title={displayName}
            onClick={() => setUserMenuOpen((o) => !o)}
          >
            {initials}
          </button>
        ) : ssoConfigured ? (
          <Link
            to="/api/auth/sso/login"
            reloadDocument
            className="btn btn--primary"
            style={{ height: 28, fontSize: 12 }}
            title="Log in with SSO"
          >
            Log in
          </Link>
        ) : (
          <Link
            to="/settings"
            className="btn btn--ghost"
            style={{ height: 28, fontSize: 12 }}
            title="Configure SSO"
          >
            Set up SSO
          </Link>
        )}

        {userMenuOpen && user && (
          <div
            role="menu"
            style={{
              position: 'absolute',
              top: 'calc(100% + 6px)',
              right: 0,
              minWidth: 220,
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-md)',
              boxShadow: 'var(--shadow-lg)',
              padding: 6,
              zIndex: 60,
            }}
          >
            <div
              style={{
                padding: '8px 10px 10px',
                borderBottom: '1px solid var(--border-subtle)',
                marginBottom: 4,
              }}
            >
              <div
                style={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: 'var(--text-1)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {displayName}
              </div>
              {secondaryLine && (
                <div
                  className="t-mono"
                  style={{
                    fontSize: 11,
                    color: 'var(--text-3)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {secondaryLine}
                </div>
              )}
              <div className="badge" style={{ marginTop: 6, fontSize: 10 }}>
                {user.role}
              </div>
            </div>
            {consoleUrl && (
              <a
                href={consoleUrl}
                className="settings-nav__item"
                data-testid="console-link"
                style={{ display: 'block', padding: '7px 10px' }}
              >
                Pipebase Cloud console
              </a>
            )}
            {consoleUrl && (
              <a
                href={`${consoleUrl.replace(/\/$/, '')}/account#security`}
                className="settings-nav__item"
                data-testid="console-account-link"
                style={{ display: 'block', padding: '7px 10px' }}
              >
                Account and sign-in
              </a>
            )}
            <Link
              to="/settings"
              className="settings-nav__item"
              onClick={() => setUserMenuOpen(false)}
              style={{ display: 'block', padding: '7px 10px' }}
            >
              Settings
            </Link>
            <button
              type="button"
              className="settings-nav__item"
              style={{ width: '100%', textAlign: 'left', padding: '7px 10px' }}
              onClick={() => {
                setUserMenuOpen(false);
                void logout();
              }}
            >
              Log out
            </button>
          </div>
        )}
      </div>
      {newAssetOpen && (
        <NewAssetModal onClose={() => setNewAssetOpen(false)} />
      )}
    </>
  );
}
